import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Snackbar,
  Tooltip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HistoryIcon from '@mui/icons-material/History';
import ConfirmDialog from '../Common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import { projectService } from '../../services/projectService';
import { invoiceService } from '../../services/invoiceService';

// Table header styles
const headerCellStyle = {
  backgroundColor: '#1565C0',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '0.85rem',
  borderBottom: '3px solid #0D47A1',
  textShadow: '1px 1px 2px rgba(0,0,0,0.2)',
};

const InvoiceSection = () => {
  const { user } = useAuth();
  const canUnlock = user?.role === 'Admin' || user?.role === 'SuperUser';

  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [projectName, setProjectName] = useState('');
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [billingToDelete, setBillingToDelete] = useState(null);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [milestoneToUnlock, setMilestoneToUnlock] = useState(null);

  // Historical records state
  const [historicalDialogOpen, setHistoricalDialogOpen] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    startMonth: '',
    endMonth: '',
  });
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [forceDelete, setForceDelete] = useState(false);
  const [deleteHistoricalDialogOpen, setDeleteHistoricalDialogOpen] = useState(false);
  const [deleteHistoricalLoading, setDeleteHistoricalLoading] = useState(false);

  // Load all projects on mount
  useEffect(() => {
    loadProjects();
  }, []);

  // Load invoice data when project is selected
  useEffect(() => {
    if (selectedProject) {
      loadInvoiceData();
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      const allProjects = await projectService.getAll();
      setProjects(allProjects);
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to load projects', severity: 'error' });
    }
  };

  const loadInvoiceData = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      // Get project name
      const project = projects.find((p) => p.project_code === selectedProject);
      setProjectName(project?.name || '');

      // Load invoice data for last 12 months
      const data = await invoiceService.getByProject(selectedProject);
      setInvoiceData(data.invoices || []);
    } catch (err) {
      if (!silent) {
        setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to load invoice data', severity: 'error' });
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const handleProjectChange = (e) => {
    setSelectedProject(e.target.value);
    setInvoiceData([]);
    setSnackbar({ open: false, message: '', severity: 'success' });
  };

  const handleGenerateBilling = async (month, currentValue) => {
    const newValue = !currentValue;

    // Optimistic update - update UI immediately
    setInvoiceData((prevData) =>
      prevData.map((row) =>
        row.month === month
          ? { ...row, billing_generated: newValue }
          : row
      )
    );

    try {
      const result = await invoiceService.generateBilling(
        selectedProject,
        month,
        newValue
      );

      // Update with actual data from API response (result.month contains the updated row)
      const updatedRow = result.month;
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === month
            ? {
                ...row,
                ...updatedRow,
              }
            : row
        )
      );
    } catch (err) {
      // Revert on error
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === month
            ? { ...row, billing_generated: currentValue }
            : row
        )
      );
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to generate billing', severity: 'error' });
    }
  };

  const handleCreateMilestone = async (month, currentValue) => {
    const newValue = !currentValue;

    // Optimistic update - update UI immediately
    setInvoiceData((prevData) =>
      prevData.map((row) =>
        row.month === month
          ? {
              ...row,
              milestone_created: newValue,
              milestone_status: newValue ? 'Created' : null,
              can_approve_milestone: newValue,
              can_create_milestone: true,
            }
          : row
      )
    );

    try {
      const result = await invoiceService.createMilestone(
        selectedProject,
        month,
        newValue
      );

      // Update with actual data from API response
      const updatedRow = result.month;
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === month
            ? {
                ...row,
                ...updatedRow,
              }
            : row
        )
      );
    } catch (err) {
      // Revert on error
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === month
            ? { ...row, milestone_created: currentValue, milestone_status: currentValue ? 'Created' : null }
            : row
        )
      );
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to create milestone', severity: 'error' });
    }
  };

  const handleApproveMilestone = async (month, currentValue) => {
    const newValue = !currentValue;

    if (newValue) {
      // Ask for confirmation before approving
      if (!window.confirm('Approving will lock this month permanently. Are you sure?')) {
        return;
      }
    }

    // Optimistic update - update UI immediately
    setInvoiceData((prevData) =>
      prevData.map((row) =>
        row.month === month
          ? {
              ...row,
              milestone_approved: newValue,
              milestone_status: newValue ? 'Closed' : 'Created',
              locked: newValue,
              can_approve_milestone: !newValue,
              can_generate_billing: !newValue,
              can_create_milestone: !newValue,
            }
          : row
      )
    );

    try {
      const result = await invoiceService.approveMilestone(
        selectedProject,
        month,
        newValue
      );

      // Update with actual data from API response
      const updatedRow = result.month;
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === month
            ? {
                ...row,
                ...updatedRow,
              }
            : row
        )
      );
    } catch (err) {
      // Revert on error
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === month
            ? {
                ...row,
                milestone_approved: currentValue,
                milestone_status: currentValue ? 'Closed' : 'Created',
                locked: currentValue,
              }
            : row
        )
      );
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to approve milestone', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Delete billing handlers
  const handleDeleteClick = (row) => {
    setBillingToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setBillingToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!billingToDelete) return;

    try {
      const result = await invoiceService.deleteBilling(
        selectedProject,
        billingToDelete.month
      );

      // Update with actual data from API response
      const updatedRow = result.month;
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === billingToDelete.month
            ? { ...row, ...updatedRow }
            : row
        )
      );

      setSnackbar({
        open: true,
        message: 'Billing deleted successfully',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete billing',
        severity: 'error',
      });
    } finally {
      setDeleteDialogOpen(false);
      setBillingToDelete(null);
    }
  };

  // Unlock milestone handlers
  const handleUnlockClick = (row) => {
    setMilestoneToUnlock(row);
    setUnlockDialogOpen(true);
  };

  const handleUnlockCancel = () => {
    setUnlockDialogOpen(false);
    setMilestoneToUnlock(null);
  };

  const handleUnlockConfirm = async () => {
    if (!milestoneToUnlock) return;

    try {
      const result = await invoiceService.unlockMilestone(
        selectedProject,
        milestoneToUnlock.month
      );

      // Update with actual data from API response
      const updatedRow = result.month;
      setInvoiceData((prevData) =>
        prevData.map((row) =>
          row.month === milestoneToUnlock.month
            ? { ...row, ...updatedRow }
            : row
        )
      );

      setSnackbar({
        open: true,
        message: 'Milestone unlocked successfully',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to unlock milestone',
        severity: 'error',
      });
    } finally {
      setUnlockDialogOpen(false);
      setMilestoneToUnlock(null);
    }
  };

  // Helper function to determine if delete button should be disabled
  const isDeleteDisabled = (row) => {
    return !row.billing_generated || row.milestone_created || row.locked;
  };

  // Helper function to get tooltip text for disabled delete button
  const getDeleteTooltip = (row) => {
    if (!row.billing_generated) {
      return 'No billing to delete';
    }
    if (row.milestone_created) {
      return 'Cannot delete after milestone creation';
    }
    if (row.locked) {
      return 'Cannot delete locked billing';
    }
    return 'Delete billing';
  };

  // Historical records handlers
  const handleOpenHistoricalDialog = () => {
    // Set default date range (last 3 years)
    const now = new Date();
    const threeYearsAgo = new Date(now.getFullYear() - 3, now.getMonth(), 1);
    setDateRange({
      startMonth: threeYearsAgo.toISOString().slice(0, 7),
      endMonth: now.toISOString().slice(0, 7),
    });
    setHistoricalData([]);
    setSelectedMonths([]);
    setForceDelete(false);
    setHistoricalDialogOpen(true);
  };

  const handleCloseHistoricalDialog = () => {
    setHistoricalDialogOpen(false);
    setHistoricalData([]);
    setSelectedMonths([]);
    setForceDelete(false);
  };

  const handleLoadHistoricalData = async () => {
    if (!dateRange.startMonth || !dateRange.endMonth) {
      setSnackbar({ open: true, message: 'Please select both start and end months', severity: 'error' });
      return;
    }

    try {
      setHistoricalLoading(true);
      // Convert YYYY-MM to YYYY-MM-01 format
      const startMonth = `${dateRange.startMonth}-01`;
      const endMonth = `${dateRange.endMonth}-01`;

      const data = await invoiceService.getHistoricalData(selectedProject, startMonth, endMonth);
      setHistoricalData(data.invoices || []);
      setSelectedMonths([]);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to load historical data',
        severity: 'error',
      });
    } finally {
      setHistoricalLoading(false);
    }
  };

  const handleSelectMonth = (month) => {
    setSelectedMonths((prev) => {
      if (prev.includes(month)) {
        return prev.filter((m) => m !== month);
      }
      return [...prev, month];
    });
  };

  const handleSelectAllMonths = () => {
    if (selectedMonths.length === historicalData.length) {
      setSelectedMonths([]);
    } else {
      setSelectedMonths(historicalData.map((row) => row.month));
    }
  };

  const handleDeleteHistoricalClick = () => {
    if (selectedMonths.length === 0) {
      setSnackbar({ open: true, message: 'Please select at least one month to delete', severity: 'error' });
      return;
    }
    setDeleteHistoricalDialogOpen(true);
  };

  const handleDeleteHistoricalCancel = () => {
    setDeleteHistoricalDialogOpen(false);
  };

  const handleDeleteHistoricalConfirm = async () => {
    try {
      setDeleteHistoricalLoading(true);
      const result = await invoiceService.bulkDeleteMilestones(
        selectedProject,
        selectedMonths,
        forceDelete
      );

      // Show result message
      let message = `Successfully deleted ${result.deletedCount} month(s)`;
      if (result.failedMonths && result.failedMonths.length > 0) {
        message += `. Failed: ${result.failedMonths.map((f) => f.month).join(', ')}`;
      }

      setSnackbar({
        open: true,
        message,
        severity: result.failedMonths?.length > 0 ? 'warning' : 'success',
      });

      // Reload historical data
      await handleLoadHistoricalData();
      // Also reload main invoice data
      await loadInvoiceData(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete milestones',
        severity: 'error',
      });
    } finally {
      setDeleteHistoricalLoading(false);
      setDeleteHistoricalDialogOpen(false);
    }
  };

  // Calculate totals for selected months
  const getSelectedTotals = () => {
    const selected = historicalData.filter((row) => selectedMonths.includes(row.month));
    const totalValue = selected.reduce((sum, row) => sum + (row.billable_value || 0), 0);
    const lockedCount = selected.filter((row) => row.locked).length;
    return { count: selected.length, totalValue, lockedCount };
  };

  return (
    <Container maxWidth="lg">
      {/* Page Header */}
      <Box sx={{
        mb: 3,
        mt: 2,
        p: 2,
        borderRadius: 2,
        background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)',
        color: 'white',
        boxShadow: '0 4px 15px rgba(21, 101, 192, 0.3)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ReceiptIcon sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight="bold">Invoice Section</Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Manage month-wise billing, milestone creation, and milestone approval
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Project Filter */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Select Project
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          <FormControl fullWidth sx={{ maxWidth: 300 }}>
            <InputLabel>Project Code *</InputLabel>
            <Select
              value={selectedProject}
              onChange={handleProjectChange}
              label="Project Code *"
              required
            >
              <MenuItem value="">
                <em>Select a project</em>
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.project_code} value={project.project_code}>
                  {project.project_code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedProject && (
            <TextField
              label="Project Name"
              value={projectName}
              fullWidth
              disabled
              sx={{ maxWidth: 400 }}
            />
          )}
          {canUnlock && selectedProject && (
            <Button
              variant="outlined"
              startIcon={<HistoryIcon />}
              onClick={handleOpenHistoricalDialog}
              sx={{ ml: 2, whiteSpace: 'nowrap' }}
            >
              Manage Historical Records
            </Button>
          )}
        </Box>
      </Paper>

      {/* Invoice Data Grid */}
      {selectedProject && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Monthly Invoice Data
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Last 12 months (latest first) • Process flow: Generate Billing → Milestone Creation → Milestone Approval → Closed
          </Typography>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellStyle}>Month</TableCell>
                    <TableCell align="center" sx={headerCellStyle}>Generate Billing</TableCell>
                    <TableCell align="center" sx={headerCellStyle}>Delete</TableCell>
                    <TableCell align="right" sx={{ ...headerCellStyle, backgroundColor: '#2E7D32', borderBottom: '3px solid #1B5E20' }}>Billable Value</TableCell>
                    <TableCell align="center" sx={{ ...headerCellStyle, backgroundColor: '#F57C00', borderBottom: '3px solid #E65100' }}>Milestone Creation</TableCell>
                    <TableCell align="center" sx={{ ...headerCellStyle, backgroundColor: '#7B1FA2', borderBottom: '3px solid #4A148C' }}>Milestone Approval</TableCell>
                    <TableCell align="center" sx={headerCellStyle}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    invoiceData.map((row, index) => (
                      <TableRow
                        key={row.month}
                        sx={{
                          bgcolor: row.locked ? '#ECEFF1' : !row.sow_covered ? '#fff8e1' : index % 2 === 0 ? '#ffffff' : '#E3F2FD',
                          opacity: row.locked ? 0.8 : 1,
                          '&:hover': { backgroundColor: row.locked ? '#ECEFF1' : '#BBDEFB' },
                          transition: 'background-color 0.2s ease',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {row.month_display}
                          </Typography>
                          {row.sow_version && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              SOW v{row.sow_version}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip
                            title={!row.sow_covered ? row.sow_coverage_reason || 'No SOW coverage' : ''}
                            arrow
                            disableHoverListener={row.sow_covered}
                          >
                            <span>
                              <Switch
                                checked={row.billing_generated}
                                onChange={() => handleGenerateBilling(row.month, row.billing_generated)}
                                disabled={!row.can_generate_billing || row.locked || !row.sow_covered}
                                color="primary"
                              />
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={getDeleteTooltip(row)} arrow>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(row)}
                                disabled={isDeleteDisabled(row)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            fontWeight={row.billing_generated ? 'bold' : 'normal'}
                            color={row.billing_generated ? 'primary' : 'textSecondary'}
                          >
                            {row.billing_generated ? `$${row.billable_value.toLocaleString()}` : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={row.milestone_created}
                            onChange={() => handleCreateMilestone(row.month, row.milestone_created)}
                            disabled={!row.can_create_milestone || row.locked}
                            color="warning"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            checked={row.milestone_approved}
                            onChange={() => handleApproveMilestone(row.month, row.milestone_approved)}
                            disabled={!row.can_approve_milestone || row.locked}
                            color="success"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {!row.sow_covered && (
                            <Tooltip title={row.sow_coverage_reason || 'No SOW coverage'} arrow>
                              <Chip label="No SOW" color="warning" size="small" sx={{ mr: 1 }} />
                            </Tooltip>
                          )}
                          {row.milestone_status === 'Closed' && (
                            <Chip label="Closed" color="success" size="small" />
                          )}
                          {row.milestone_status === 'Created' && (
                            <Chip label="Created" color="warning" size="small" />
                          )}
                          {row.locked && (
                            <>
                              <Chip label="Locked" color="error" size="small" sx={{ ml: 1 }} />
                              {canUnlock && (
                                <Tooltip title="Unlock milestone" arrow>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleUnlockClick(row)}
                                    sx={{ ml: 0.5 }}
                                  >
                                    <LockOpenIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete Billing Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Billing Records"
        message="Are you sure you want to delete all billing records for this month? This action cannot be undone."
        details={
          billingToDelete
            ? [
                { label: 'Month', value: billingToDelete.month_display },
                {
                  label: 'Billable Value',
                  value: `$${billingToDelete.billable_value?.toLocaleString() || 0}`,
                },
              ]
            : []
        }
        confirmText="Delete"
        confirmColor="error"
      />

      {/* Unlock Milestone Confirmation Dialog */}
      <ConfirmDialog
        open={unlockDialogOpen}
        onClose={handleUnlockCancel}
        onConfirm={handleUnlockConfirm}
        title="Unlock Milestone"
        message="Are you sure you want to unlock this milestone? This will allow editing of billing configurations for this month."
        details={
          milestoneToUnlock
            ? [
                { label: 'Month', value: milestoneToUnlock.month_display },
                {
                  label: 'Billable Value',
                  value: `$${milestoneToUnlock.billable_value?.toLocaleString() || 0}`,
                },
              ]
            : []
        }
        confirmText="Unlock"
        confirmColor="warning"
      />

      {/* Historical Records Management Dialog */}
      <Dialog
        open={historicalDialogOpen}
        onClose={handleCloseHistoricalDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: '#1565C0', color: 'white' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon />
            Manage Historical Invoice Records
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {/* Date Range Selection */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Month"
              type="month"
              value={dateRange.startMonth}
              onChange={(e) => setDateRange({ ...dateRange, startMonth: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="End Month"
              type="month"
              value={dateRange.endMonth}
              onChange={(e) => setDateRange({ ...dateRange, endMonth: e.target.value })}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <Button
              variant="contained"
              onClick={handleLoadHistoricalData}
              disabled={historicalLoading}
            >
              {historicalLoading ? <CircularProgress size={20} /> : 'Load Records'}
            </Button>
          </Box>

          {/* Results Table */}
          {historicalData.length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="textSecondary">
                  Found {historicalData.length} record(s) with billing data
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  {user?.role === 'Admin' && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={forceDelete}
                          onChange={(e) => setForceDelete(e.target.checked)}
                          color="error"
                        />
                      }
                      label={
                        <Typography variant="body2" color="error">
                          Force Delete Locked
                        </Typography>
                      }
                    />
                  )}
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteHistoricalClick}
                    disabled={selectedMonths.length === 0}
                  >
                    Delete Selected ({selectedMonths.length})
                  </Button>
                </Box>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={headerCellStyle}>
                        <Checkbox
                          checked={selectedMonths.length === historicalData.length && historicalData.length > 0}
                          indeterminate={selectedMonths.length > 0 && selectedMonths.length < historicalData.length}
                          onChange={handleSelectAllMonths}
                          sx={{ color: 'white', '&.Mui-checked': { color: 'white' } }}
                        />
                      </TableCell>
                      <TableCell sx={headerCellStyle}>Month</TableCell>
                      <TableCell align="right" sx={headerCellStyle}>Billable Value</TableCell>
                      <TableCell align="center" sx={headerCellStyle}>Billing</TableCell>
                      <TableCell align="center" sx={headerCellStyle}>Milestone</TableCell>
                      <TableCell align="center" sx={headerCellStyle}>Approved</TableCell>
                      <TableCell align="center" sx={headerCellStyle}>Locked</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historicalData.map((row, index) => (
                      <TableRow
                        key={row.month}
                        sx={{
                          bgcolor: row.locked ? '#FFEBEE' : index % 2 === 0 ? '#ffffff' : '#E3F2FD',
                          '&:hover': { backgroundColor: '#BBDEFB' },
                        }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedMonths.includes(row.month)}
                            onChange={() => handleSelectMonth(row.month)}
                            disabled={row.locked && !forceDelete && user?.role !== 'Admin'}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {row.month_display}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            ${row.billable_value?.toLocaleString() || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {row.billing_generated ? (
                            <Chip label="Yes" color="success" size="small" />
                          ) : (
                            <Chip label="No" size="small" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {row.milestone_created ? (
                            <Chip label="Created" color="warning" size="small" />
                          ) : (
                            <Chip label="-" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {row.milestone_approved ? (
                            <Chip label="Approved" color="success" size="small" />
                          ) : (
                            <Chip label="-" size="small" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {row.locked ? (
                            <Chip label="Locked" color="error" size="small" />
                          ) : (
                            <Chip label="Unlocked" size="small" variant="outlined" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Summary */}
              {selectedMonths.length > 0 && (
                <Alert
                  severity={getSelectedTotals().lockedCount > 0 ? 'warning' : 'info'}
                  sx={{ mt: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Selected:</strong> {getSelectedTotals().count} month(s) •{' '}
                    <strong>Total Value:</strong> ${getSelectedTotals().totalValue.toLocaleString()}
                    {getSelectedTotals().lockedCount > 0 && (
                      <>
                        {' '}• <strong style={{ color: '#d32f2f' }}>{getSelectedTotals().lockedCount} locked</strong>
                        {user?.role === 'Admin'
                          ? ' (force delete enabled for Admin)'
                          : ' (cannot delete without Admin force delete)'}
                      </>
                    )}
                  </Typography>
                </Alert>
              )}
            </>
          )}

          {historicalData.length === 0 && !historicalLoading && (
            <Alert severity="info">
              Select a date range and click "Load Records" to view historical invoice data.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseHistoricalDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Historical Records Confirmation Dialog */}
      <ConfirmDialog
        open={deleteHistoricalDialogOpen}
        onClose={handleDeleteHistoricalCancel}
        onConfirm={handleDeleteHistoricalConfirm}
        title="Delete Historical Records"
        message={`Are you sure you want to delete ${selectedMonths.length} month(s) of billing and milestone records? This action cannot be undone.`}
        details={[
          { label: 'Months Selected', value: selectedMonths.length.toString() },
          { label: 'Total Value', value: `$${getSelectedTotals().totalValue.toLocaleString()}` },
          ...(getSelectedTotals().lockedCount > 0
            ? [{ label: 'Locked Records', value: `${getSelectedTotals().lockedCount} (force delete: ${forceDelete ? 'enabled' : 'disabled'})` }]
            : []),
        ]}
        confirmText={deleteHistoricalLoading ? 'Deleting...' : 'Delete All'}
        confirmColor="error"
      />
    </Container>
  );
};

export default InvoiceSection;
